import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
}) => {
  if (totalPages <= 0) return null; // Safe fallback, though totalPages is usually at least 1

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate visible page numbers with ellipsis
  const getPageNumbers = (): (number | '...')[] => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage <= 4) {
        for (let i = 2; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push('...');
        for (let i = totalPages - 4; i < totalPages; i++) pages.push(i);
        pages.push(totalPages);
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-8 py-6 border-t border-slate-50 bg-[#fbfbfa] rounded-b-[9px]">
      <span className="text-[13px] font-semibold text-slate-500">
        Showing {startItem}–{endItem} of {totalItems}
      </span>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-10 h-10 flex items-center justify-center rounded-[6px] text-[#bac5d0] bg-transparent border border-[#dcdcdc] hover:bg-[#475763] hover:text-white hover:border-transparent disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#dcdcdc] disabled:hover:text-[#bac5d0] disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {getPageNumbers().map((page, idx) =>
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="w-10 h-10 flex items-center justify-center text-[#bac5d0] text-[15px] font-bold tracking-widest">
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`w-10 h-10 flex items-center justify-center rounded-[6px] text-[15px] font-bold transition-all ${page === currentPage
                ? 'bg-[#111517] text-white border-transparent'
                : 'bg-transparent text-[#1e2a32] border border-[#dcdcdc] hover:bg-[#475763] hover:text-white hover:border-transparent'
                }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-10 h-10 flex items-center justify-center rounded-[6px] text-[#bac5d0] bg-transparent border border-[#dcdcdc] hover:bg-[#475763] hover:text-white hover:border-transparent disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:border-[#dcdcdc] disabled:hover:text-[#bac5d0] disabled:cursor-not-allowed transition-all"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
